// Client-only Firebase initialization. Do not import this directly in shared/server code.
import { getApp, getApps, initializeApp } from "firebase/app";
import { Messaging, getMessaging, getToken, isSupported } from "firebase/messaging";
import { getAnalytics, Analytics } from "firebase/analytics";
import { getFirestore } from "firebase/firestore";
import { IS_MAINNET } from "@shared/src/constants";

const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
  measurementId: process.env.NEXT_PUBLIC_FIREBASE_MEASUREMENT_ID,
};

const mainnetFirebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_MAINNET_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_MAINNET_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_MAINNET_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_MAINNET_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_MAINNET_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_MAINNET_FIREBASE_APP_ID,
  measurementId: process.env.NEXT_PUBLIC_MAINNET_FIREBASE_MEASUREMENT_ID,
};

// Initialize default app (idempotent)
const app = !getApps().length ? initializeApp(firebaseConfig) : getApp("[DEFAULT]");
const mainnetApp = IS_MAINNET ? initializeApp(mainnetFirebaseConfig, "iexchange-mainnet") : null;
const db = getFirestore(app, "iexchange-db");

let _messaging: Messaging | null = null;
let _analytics: Analytics | null = null;

if (typeof window !== "undefined" && "serviceWorker" in navigator) {
  try {
    _analytics = mainnetApp ? getAnalytics(mainnetApp) : getAnalytics(app);
    _messaging = getMessaging(app);
  } catch (error) {
    console.log("Error initializing Firebase Messaging. This error is handled and safe", error);
  }
}

const messaging = _messaging;
const analytics = _analytics;

export const requestNotificationPermission = async () => {
  if (!messaging) return null;
  if (Notification.permission === "granted") {
    return getToken(messaging, { vapidKey: process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY });
  }
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    console.log("Notification permission denied");
    return null;
  }
  return getToken(messaging, { vapidKey: process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY });
};

const messagingInstance = async () => {
  const supported = await isSupported();
  return supported ? getMessaging(app) : null;
};

export const fetchToken = async () => {
  try {
    const fcmMessaging = await messagingInstance();
    if (fcmMessaging) {
      const token = await getToken(fcmMessaging, { vapidKey: process.env.NEXT_PUBLIC_FIREBASE_FCM_VAPID_KEY });
      return token;
    }
    return null;
  } catch (err) {
    console.error("An error occurred while fetching the token:", err);
    return null;
  }
};

export { messaging, messagingInstance, analytics, db, app };
